import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Star, Calendar as CalendarIcon, MapPin, MessageSquare, BadgeCheck, CalendarDays, CheckCircle2, XCircle, Loader2, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ReportDialog from "@/components/ReportDialog";
import InstantBookSection from "@/components/InstantBookSection";
import { CATEGORY_LABEL, offeringLabels } from "@/lib/categories";
import { parseLocalDate, toLocalIsoDate, formatEventDate, isBookableEvent } from "@/lib/dates";

const INACTIVE_BOOKING = new Set(["cancelled", "declined"]);
const BOOKING_STATE_LABEL = { requested: "Requested", quoted: "Quote received", accepted: "Accepted", paid: "Deposit paid", completed: "Completed" };
const DURATION_CATEGORIES = new Set(["venue", "dj", "security"]);
const DURATION_OPTIONS = [...Array.from({ length: 12 }, (_, i) => [String(i + 1), `${i + 1} ${i === 0 ? "hour" : "hours"}`]), ["12+", "12+ hours"]];

function DurationSelect({ value, onChange, testid, compact = false }) {
  return (
    <div>
      <div className="eyebrow mb-2">How many hours do you need? <span className="text-[color:var(--accent)]">*</span></div>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-md border border-[#E8E2D9] bg-white px-3 ${compact ? "h-9 text-xs" : "h-11 text-sm"}`}
      >
        <option value="">Select hours…</option>
        {DURATION_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
    </div>
  );
}

// Human label for an existing booking, preferring the live payment summary.
function bookingStateLabel(b, s) {
  if (s?.fully_paid) return "Paid in full";
  if (s?.deposit_paid > 0 && s.remaining > 0) return `Deposit paid · ${formatUSD(s.remaining)} remaining`;
  return BOOKING_STATE_LABEL[b.status] || b.status;
}
const formatUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function VendorProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const eventParam = searchParams.get("event") || "";
  const [v, setV] = useState(null);
  const [menus, setMenus] = useState([]);
  const [products, setProducts] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [unavailableWeekdays, setUnavailableWeekdays] = useState([]);
  const [open, setOpen] = useState(false);
  // Event context: the selected event's stored date is the single source of
  // truth for the service date — there is no independent booking date.
  const [events, setEvents] = useState(null); // null = loading
  const [eventsFailed, setEventsFailed] = useState(false);
  // The URL is the single source of truth for the selected event so the context
  // survives re-renders, remounts and refresh.
  const selectedEventId = eventParam;
  const [avail, setAvail] = useState(null); // {loading, available, reason}
  const [period, setPeriod] = useState("pm");
  const [durationHours, setDurationHours] = useState("");
  const [guests, setGuests] = useState(50);
  const [msg, setMsg] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [availability, setAvailability] = useState({ blocked_dates: [], weekday_am: [], weekday_pm: [], blocked_date_slots: [] });
  const [selectedItems, setSelectedItems] = useState([]);
  const [menuTree, setMenuTree] = useState([]);
  const [instantCount, setInstantCount] = useState(0);
  const [myBookings, setMyBookings] = useState([]);
  const [existingSummary, setExistingSummary] = useState(null);

  const isPlanner = user?.role === "planner";
  const selectedEvent = (events || []).find((e) => e.event_id === selectedEventId) || null;
  const date = selectedEvent ? parseLocalDate(selectedEvent.date) : undefined;

  const load = () => api.get(`/vendors/${id}`).then(({ data }) => setV(data));
  const loadMenus = () => api.get(`/vendors/${id}/menus`).then(({ data }) => setMenus(data)).catch(() => setMenus([]));
  // Owner previewing their own listing can see AI drafts before approval.
  // Public visitors get only approved products.
  const loadProducts = (vendorDoc) => {
    const owns = user && vendorDoc && user.user_id === vendorDoc.owner_id;
    const isAdmin = user && user.role === "admin";
    const withDrafts = owns || isAdmin;
    api.get(`/vendors/${id}/products${withDrafts ? "?include_drafts=true" : ""}`)
      .then(({ data }) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]));
  };
  const [approvingDrafts, setApprovingDrafts] = useState(false);
  const approveAllDrafts = async () => {
    setApprovingDrafts(true);
    try {
      const { data } = await api.post(`/vendors/${id}/products/approve-all`);
      toast.success(`Approved ${data.approved_count} draft${data.approved_count === 1 ? "" : "s"}`);
      loadProducts(v);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not approve drafts"); }
    finally { setApprovingDrafts(false); }
  };
  const loadAvailability = () => api.get(`/vendors/${id}/availability`).then(({ data }) => {
    setBlockedDates(data.blocked_dates || []);
    setUnavailableWeekdays(data.unavailable_weekdays || []);
    setAvailability(data);
  }).catch(() => { setBlockedDates([]); setUnavailableWeekdays([]); });
  useEffect(() => { load(); loadMenus(); loadAvailability(); }, [id]);
  // Re-fetch products whenever we know if the current user owns this listing.
  useEffect(() => { if (v) loadProducts(v); /* eslint-disable-next-line */ }, [v, user]);
  // Load full menu tree for the multi-select quote item picker.
  useEffect(() => {
    if (!v?.vendor_id) return;
    api.get(`/vendors/${v.vendor_id}/menus`).then(({ data }) => setMenuTree(Array.isArray(data) ? data : [])).catch(() => setMenuTree([]));
  }, [v?.vendor_id]);

  // Planner's upcoming, active events for the "Booking for" selector.
  useEffect(() => {
    if (!isPlanner) { setEvents([]); return; }
    let cancelled = false;
    api.get("/events")
      .then(({ data }) => {
        if (cancelled) return;
        const list = (Array.isArray(data) ? data : []).filter(isBookableEvent)
          .sort((a, b) => String(a.date).localeCompare(String(b.date)));
        setEventsFailed(false);
        setEvents(list);
      })
      .catch(() => { if (!cancelled) { setEventsFailed(true); setEvents([]); } });
    return () => { cancelled = true; };
  }, [isPlanner]);

  // Resolve the initial selection once events are known: honour a valid
  // ?event= param, auto-select when there is exactly one, otherwise require a choice.
  // Never clear the URL context on a failed/empty fetch — only on a confirmed miss.
  useEffect(() => {
    if (!events || eventsFailed) return;
    if (eventParam && !events.some((e) => e.event_id === eventParam)) {
      toast.error("That event isn't available for booking.");
      selectEvent("");
      return;
    }
    if (!eventParam && events.length === 1) selectEvent(events[0].event_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, eventsFailed]);

  useEffect(() => {
    if (selectedEvent) setGuests(Number(selectedEvent.guest_count) || 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.event_id]);

  // Planner's own bookings — used to recognise "already booked for this event".
  useEffect(() => {
    if (!isPlanner) { setMyBookings([]); return; }
    api.get("/bookings").then(({ data }) => setMyBookings(Array.isArray(data) ? data : [])).catch(() => setMyBookings([]));
  }, [isPlanner]);

  const existingBooking = (isPlanner && selectedEvent && v)
    ? myBookings.find((b) => b.vendor_id === v.vendor_id && b.event_id === selectedEvent.event_id && !INACTIVE_BOOKING.has(b.status))
    : null;

  useEffect(() => {
    if (!existingBooking) { setExistingSummary(null); return; }
    api.get(`/bookings/${existingBooking.booking_id}/payment-summary`).then(({ data }) => setExistingSummary(data)).catch(() => setExistingSummary(null));
  }, [existingBooking?.booking_id]);

  // Fresh server-side availability for the selected event date + period.
  useEffect(() => {
    if (!v?.vendor_id || !selectedEvent) { setAvail(null); return; }
    if (existingBooking) { setAvail({ loading: false, available: true }); return; } // own booking holds the slot
    let cancelled = false;
    setAvail({ loading: true });
    api.get(`/vendors/${v.vendor_id}/availability/check`, { params: { date: String(selectedEvent.date).slice(0, 10), period } })
      .then(({ data }) => { if (!cancelled) setAvail({ loading: false, available: !!data.available, reason: data.reason }); })
      .catch(() => { if (!cancelled) setAvail({ loading: false, available: false, reason: "Could not verify availability" }); });
    return () => { cancelled = true; };
  }, [v?.vendor_id, selectedEvent?.event_id, selectedEvent?.date, period, existingBooking?.booking_id]);

  const selectEvent = (eventId) => {
    const p = new URLSearchParams(searchParams);
    if (eventId) p.set("event", eventId); else p.delete("event");
    setSearchParams(p, { replace: true });
  };

  // "Change event date" — persists to the event, then availability re-checks.
  const changeEventDate = async (d) => {
    if (!d || !selectedEvent) return;
    const iso = toLocalIsoDate(d);
    try {
      const { data } = await api.put(`/events/${selectedEvent.event_id}`, { date: iso });
      setEvents((prev) => (prev || []).map((e) => (e.event_id === data.event_id ? { ...e, ...data } : e)));
      toast.success(`Event date updated to ${formatEventDate(iso)}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update event date"); }
  };

  // Venue-first: a venue-needed event can only book venue vendors until it has a location.
  const isVenueVendor = [v?.category, ...(v?.additional_categories || [])].includes("venue");
  const requiresDuration = [v?.category, ...(v?.additional_categories || [])].some((c) => DURATION_CATEGORIES.has(c));
  const durationMissing = requiresDuration && !durationHours;
  const venueFirst = !!selectedEvent?.needs_venue && !selectedEvent?.location && !isVenueVendor;
  const VENUE_FIRST_MSG = "Choose your venue first. Your event needs a location before you can book other vendors.";
  const bookingBlocked = !selectedEvent || avail?.loading || avail?.available === false || venueFirst;
  const unavailableReason = venueFirst ? VENUE_FIRST_MSG : (avail?.available === false
    ? `Unavailable for ${formatEventDate(selectedEvent?.date)}${avail.reason ? ` — ${avail.reason}` : ""}`
    : "");

  const blockedSet = new Set(blockedDates);
  const isDateFullyBlocked = (d) => {
    if (!d) return false;
    if (unavailableWeekdays.includes(d.getDay())) return true;
    return blockedSet.has(toLocalIsoDate(d));
  };
  // A date is "picker-disabled" only when BOTH AM and PM are unavailable —
  // otherwise the user can still pick it and choose the free period.
  const isDateBlocked = (d) => {
    if (!d) return false;
    if (isDateFullyBlocked(d)) return true;
    const wd = d.getDay();
    const iso = toLocalIsoDate(d);
    const amOff = availability.weekday_am?.includes(wd) || availability.blocked_date_slots?.some(s => s.date === iso && s.period === "am");
    const pmOff = availability.weekday_pm?.includes(wd) || availability.blocked_date_slots?.some(s => s.date === iso && s.period === "pm");
    return amOff && pmOff;
  };
  const isPeriodBlocked = (d, p) => {
    if (!d || !p) return false;
    if (isDateFullyBlocked(d)) return true;
    const wd = d.getDay();
    const iso = toLocalIsoDate(d);
    if (p === "am") return (availability.weekday_am || []).includes(wd) || (availability.blocked_date_slots || []).some(s => s.date === iso && s.period === "am");
    return (availability.weekday_pm || []).includes(wd) || (availability.blocked_date_slots || []).some(s => s.date === iso && s.period === "pm");
  };

  if (!v) return <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24" data-testid="vendor-loading">Loading…</div>;

  const gallery = [v.cover_image, ...(v.gallery || [])].filter(Boolean);
  const isOwnerView = !!user && user.user_id === v.owner_id;

  // A vendor may only view their OWN listing. Any other vendor profile is
  // off-limits and bounces to their dashboard. This is a UX shortcut only —
  // backend `/api/vendors/{id}` also 404s vendor→other-vendor access.
  if (user?.role === "vendor" && !isOwnerView) {
    return <Navigate to="/vendor/dashboard" replace />;
  }

  const submitBooking = async () => {
    if (!user) return navigate("/login");
    if (!selectedEvent) return toast.error("Select or create an event first");
    if (avail?.available === false) return toast.error(unavailableReason);
    if (isDateBlocked(date)) return toast.error("The vendor is unavailable on that date");
    if (isPeriodBlocked(date, period)) return toast.error(`Vendor is unavailable during the selected ${period === "am" ? "morning" : "afternoon"}`);
    if (durationMissing) return toast.error("Please select how many hours you need");
    try {
      // Server derives the service date from event_id; `date` is sent only to satisfy the schema.
      const { data } = await api.post("/bookings", {
        vendor_id: v.vendor_id, event_id: selectedEvent.event_id, date: String(selectedEvent.date).slice(0, 10),
        period, guest_count: guests, message: msg, selected_items: selectedItems,
        ...(requiresDuration ? { duration_hours: durationHours } : {}),
      });
      toast.success("Quote request sent");
      setOpen(false); setSelectedItems([]);
      navigate(`/bookings/${data.booking_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitReview = async () => {
    if (!user) return navigate("/login");
    try {
      await api.post(`/vendors/${v.vendor_id}/reviews`, { rating, comment });
      toast.success("Review posted");
      setReviewOpen(false); setComment("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="vendor-profile-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <Link to="/browse" data-testid="back-link" className="text-sm link-underline text-[#5C5C5C]">← Back to collective</Link>

      {/* Header */}
      <div className="mt-6 sm:mt-8 grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
        <div className="lg:col-span-7">
          <div className="eyebrow mb-4" data-testid="vendor-profile-categories">
            {[v.category, ...(v.additional_categories || [])].map(c => CATEGORY_LABEL[c] || c).join(" · ")}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight" data-testid="vendor-name">{v.name}</h1>
          {v.verified && (
            <div className="mt-4 inline-flex items-center gap-2 badge-verified" data-testid="profile-verified-badge">
              <BadgeCheck className="w-3.5 h-3.5" /> Verified by NContro
            </div>
          )}
          <p className="mt-4 sm:mt-6 text-base sm:text-lg text-[#5C5C5C] max-w-xl">{v.tagline}</p>
          <div className="mt-4 sm:mt-6 flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
            <span className="flex items-center gap-2 text-[#5C5C5C]"><MapPin className="w-4 h-4" /> {v.city}</span>
            <span className="flex items-center gap-2"><Star className="w-4 h-4 text-[#C05A46]" /> {v.rating || "—"} ({v.reviews?.length || 0} reviews)</span>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="card-elegant p-6 sm:p-8">
            <div className="eyebrow">Starting at</div>
            <div className="font-serif text-4xl sm:text-5xl mt-2">${v.price_from}<span className="text-base text-[#5C5C5C] ml-2">{v.price_unit}</span></div>
            {isOwnerView ? (
              <div data-testid="vendor-owner-notice" className="mt-6 rounded-md bg-[#F5F2EB] border border-[#E8E2D9] p-4 text-sm text-[#5C5C5C]">
                This is how planners see your listing. Edit it from the{" "}
                <Link to="/vendor/dashboard" className="link-underline text-[#1A1A1A]">Vendor Studio</Link>.
              </div>
            ) : isPlanner ? (
              <>
                <EventPicker
                  events={events}
                  selectedEventId={selectedEventId}
                  onSelect={selectEvent}
                  period={period}
                  onPeriod={setPeriod}
                  requiresDuration={requiresDuration}
                  durationHours={durationHours}
                  onDuration={setDurationHours}
                  avail={avail}
                  existingBooking={existingBooking}
                />
                {existingBooking ? (
                  <div data-testid="existing-booking-card" className="mt-6 rounded-md border border-[#E8E2D9] bg-white p-4 space-y-3">
                    <div className="eyebrow">You've booked this vendor for {selectedEvent.title}</div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold" data-testid="existing-booking-state">{bookingStateLabel(existingBooking, existingSummary)}</span>
                      <span className="text-sm text-[#5C5C5C] whitespace-nowrap" data-testid="existing-booking-total">{formatUSD(existingBooking.quote_amount)}</span>
                    </div>
                    <Button
                      data-testid="view-booking-messages-btn"
                      onClick={() => navigate(`/bookings/${existingBooking.booking_id}`)}
                      className="btn-sage w-full rounded-full h-12"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" /> View booking & messages
                    </Button>
                  </div>
                ) : (
                <>
                {/* Instant Book leads when the vendor has instant offerings; the
                    quote action then becomes the secondary path. */}
                <InstantBookSection
                  vendor={v} user={user} date={date} period={period}
                  durationHours={requiresDuration ? durationHours : undefined}
                  requiresDuration={requiresDuration}
                  eventId={selectedEvent?.event_id || ""}
                  available={!bookingBlocked}
                  unavailableReason={!selectedEvent ? "Select an event first" : unavailableReason}
                  onLoaded={setInstantCount}
                />
                {venueFirst && (
                  <div className="mt-6 rounded-md border border-[#E8E2D9] bg-[#F5F2EB] p-4" data-testid="venue-first-notice">
                    <div className="font-serif text-lg">Choose your venue first</div>
                    <div className="text-sm text-[#5C5C5C] mt-1">Your event needs a location before you can book other vendors.</div>
                    <Button asChild variant="outline" className="rounded-full h-10 mt-3">
                      <Link to={`/browse?event=${selectedEvent.event_id}&category=venue`} data-testid="venue-first-find-venue-btn">
                        <Search className="w-4 h-4 mr-2" /> Find a venue
                      </Link>
                    </Button>
                  </div>
                )}
                <Button
                  data-testid="request-quote-btn"
                  onClick={() => (venueFirst ? toast.error(VENUE_FIRST_MSG) : setOpen(true))}
                  disabled={bookingBlocked && !venueFirst}
                  aria-disabled={bookingBlocked}
                  variant={instantCount > 0 ? "outline" : "default"}
                  title={!selectedEvent ? "Select an event first" : (bookingBlocked ? unavailableReason : undefined)}
                  className={`w-full rounded-full h-12 mt-6 ${instantCount > 0 ? "border-[#E8E2D9] bg-white text-[#1A1A1A] hover:bg-[#F5F2EB]" : "btn-sage"}`}
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> {instantCount > 0 ? "Request a custom quote" : "Request a quote"}
                </Button>
                </>
                )}
              </>
            ) : null}
            <div className="mt-6 pt-6 border-t border-[#E8E2D9] space-y-3 text-sm">
              <div><span className="eyebrow">Contact</span><div className="mt-1 break-all">{v.contact_email}</div></div>
              {(isOwnerView || user?.role === "admin") && v.contact_phone && (
                <div data-testid="vendor-phone">{v.contact_phone}</div>
              )}
            </div>
            {user?.role === "planner" && !isOwnerView && (
              <div className="mt-6 pt-4 border-t border-[#E8E2D9] flex justify-end">
                <ReportDialog
                  targetType="vendor"
                  targetId={v.vendor_id}
                  targetLabel={v.name}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="mt-12 sm:mt-16 grid grid-cols-2 sm:grid-cols-4 grid-rows-2 gap-2 sm:gap-3 h-[320px] sm:h-[420px] lg:h-[520px]" data-testid="gallery">
          <img src={gallery[0]} alt="" className="col-span-2 row-span-2 w-full h-full object-cover rounded-md" />
          {gallery.slice(1, 5).map((g, i) => (
            <img key={i} src={g} alt="" className="w-full h-full object-cover rounded-md" />
          ))}
        </section>
      )}

      {/* About + Services */}
      <section className="mt-16 sm:mt-20 grid lg:grid-cols-12 gap-8 lg:gap-16">
        <div className="lg:col-span-7">
          <div className="eyebrow mb-3">About</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-6">The story</h2>
          <p className="text-[#1A1A1A] leading-relaxed text-base sm:text-lg">{v.description}</p>
          {v.subcategory && (
            <div className="mt-4 inline-block eyebrow text-[color:var(--accent)]" data-testid="profile-subcategory">
              {v.subcategory}
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          <div className="eyebrow mb-3">Services</div>
          <ul className="space-y-3">
            {(v.services || []).map((s) => (
              <li key={s} className="flex items-baseline gap-3 border-b border-[#E8E2D9] pb-3">
                <span className="text-xs text-[#8A9A86]">✦</span>
                <span className="font-serif text-xl">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Category-specific specs — Photography / Transportation */}
      {(v.category === "photography" || v.category === "transportation") && (
        <section className="mt-12 sm:mt-16" data-testid="category-specs">
          <div className="eyebrow mb-3">Details</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {v.category === "photography" && [
              ["Specialty", v.subcategory],
              ["Years in business", v.years_in_business],
              ["Portfolio", v.portfolio_url ? <a href={v.portfolio_url} target="_blank" rel="noreferrer" className="link-underline">View →</a> : null],
              ["Video reel", v.video_reel_url ? <a href={v.video_reel_url} target="_blank" rel="noreferrer" className="link-underline">Watch →</a> : null],
              ["Instagram", v.instagram],
            ].filter(([, val]) => !!val).map(([label, val]) => (
              <div key={label} className="card-elegant p-4">
                <div className="eyebrow mb-1 text-[10px]">{label}</div>
                <div className="text-sm font-semibold">{val}</div>
              </div>
            ))}
            {v.category === "transportation" && [
              ["Vehicle types", v.vehicle_types],
              ["Max passengers", v.passenger_capacity],
              ["Fleet size", v.fleet_size],
              ["Chauffeur", v.chauffeur_included],
              ["Minimum rental", v.minimum_rental_hours ? `${v.minimum_rental_hours} hrs` : null],
              ["Hourly rate", v.hourly_rate ? `$${v.hourly_rate}/hr` : null],
              ["Overtime rate", v.overtime_rate ? `$${v.overtime_rate}/hr` : null],
              ["Mileage charge", v.mileage_charge ? `$${v.mileage_charge}/mi` : null],
            ].filter(([, val]) => !!val).map(([label, val]) => (
              <div key={label} className="card-elegant p-4">
                <div className="eyebrow mb-1 text-[10px]">{label}</div>
                <div className="text-sm font-semibold">{val}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Vendor's payment & cancellation policy */}
      {v.payment_policy_resolved && (
        <section className="mt-16 sm:mt-24 border-t border-[#E8E2D9] pt-12 sm:pt-16" data-testid="public-policy-section">
          <div className="mb-8 sm:mb-10">
            <div className="eyebrow mb-3">Payment &amp; cancellation policy</div>
            <h2 className="font-serif text-3xl sm:text-4xl">Set by this vendor</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 max-w-3xl">
            <div className="card-elegant p-5">
              <div className="eyebrow mb-2">Deposit</div>
              <p className="text-sm">{v.payment_policy_resolved.computed.deposit_summary}</p>
              <p className="text-xs text-[#5C5C5C] mt-1">{v.payment_policy_resolved.computed.balance_summary}</p>
            </div>
            <div className="card-elegant p-5">
              <div className="eyebrow mb-2">Cancellation</div>
              <p className="text-sm">{v.payment_policy_resolved.computed.cancellation_summary}</p>
            </div>
          </div>
          {(v.payment_policy_resolved.rescheduling_policy || v.payment_policy_resolved.weather_policy || v.payment_policy_resolved.late_payment_policy || v.payment_policy_resolved.damage_policy || v.payment_policy_resolved.other_terms) && (
            <details className="mt-6 max-w-3xl" data-testid="additional-policy-details">
              <summary className="text-sm link-underline cursor-pointer">Additional vendor policies</summary>
              <div className="mt-3 space-y-3 text-sm text-[#5C5C5C]">
                {v.payment_policy_resolved.rescheduling_policy && <div><strong className="text-[#1A1A1A]">Rescheduling.</strong> {v.payment_policy_resolved.rescheduling_policy}</div>}
                {v.payment_policy_resolved.weather_policy && <div><strong className="text-[#1A1A1A]">Weather.</strong> {v.payment_policy_resolved.weather_policy}</div>}
                {v.payment_policy_resolved.late_payment_policy && <div><strong className="text-[#1A1A1A]">Late payment.</strong> {v.payment_policy_resolved.late_payment_policy}</div>}
                {v.payment_policy_resolved.damage_policy && <div><strong className="text-[#1A1A1A]">Damages.</strong> {v.payment_policy_resolved.damage_policy}</div>}
                {v.payment_policy_resolved.other_terms && <div><strong className="text-[#1A1A1A]">Other.</strong> {v.payment_policy_resolved.other_terms}</div>}
              </div>
            </details>
          )}
          {!v.payment_policy_resolved.is_custom && (
            <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C] mt-4">Platform default policy — this vendor has not customized theirs.</div>
          )}
        </section>
      )}

      {/* Public Menus */}
      {menus.length > 0 && (
        <section className="mt-16 sm:mt-24 border-t border-[#E8E2D9] pt-12 sm:pt-16" data-testid="public-menus-section">
          <div className="mb-8 sm:mb-10">
            <div className="eyebrow mb-3" data-testid="public-menus-eyebrow">{offeringLabels(v.category).publicEyebrow}</div>
            <h2 className="font-serif text-3xl sm:text-4xl">What they offer</h2>
          </div>
          <div className="space-y-12">
            {menus.map((m) => (
              <div key={m.menu_id} data-testid={`menu-${m.menu_id}`}>
                <h3 className="font-serif text-2xl sm:text-3xl mb-2">{m.name}</h3>
                {m.description && <p className="text-[#5C5C5C] mb-6 max-w-2xl">{m.description}</p>}
                <div className="space-y-8">
                  {(m.categories || []).map((c) => (
                    <div key={c.category_id}>
                      <div className="eyebrow mb-4">{c.name}</div>
                      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                        {(c.items || []).filter(it => it.available !== false).map((it) => (
                          <div key={it.item_id} data-testid={`menu-item-${it.item_id}`} className="border-b border-[#E8E2D9] pb-4">
                            <div className="flex items-baseline justify-between gap-4">
                              <div className="font-serif text-lg leading-tight">{it.name}</div>
                              {it.price > 0 && <div className="text-sm text-[#1A1A1A] whitespace-nowrap">${it.price}</div>}
                            </div>
                            {it.description && <p className="text-sm text-[#5C5C5C] mt-1 leading-snug">{it.description}</p>}
                            {(it.tags || []).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {it.tags.map((t) => (
                                  <span key={t} className="text-[10px] uppercase tracking-widest text-[#8A9A86] bg-[#EFEADF] px-2 py-0.5 rounded-full">{t}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Packages & Services (products) — shown for any vendor with approved
          products, plus draft items when the current user is the owner or admin
          viewing their own listing preview. */}
      {products.length > 0 && (() => {
        // Group by the `category` field the AI/manual builder writes.
        const grouped = products.reduce((acc, p) => {
          const key = p.category || "Packages & Services";
          (acc[key] ||= []).push(p);
          return acc;
        }, {});
        const draftCount = products.filter(p => p.ai_draft).length;
        return (
          <section className="mt-16 sm:mt-24 border-t border-[#E8E2D9] pt-12 sm:pt-16" data-testid="public-products-section">
            <div className="mb-8 sm:mb-10">
              <div className="eyebrow mb-3">Packages &amp; Services</div>
              <h2 className="font-serif text-3xl sm:text-4xl">What they offer</h2>
              {draftCount > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]" data-testid="drafts-preview-note">
                    Preview includes {draftCount} draft {draftCount === 1 ? "item" : "items"} — only visible to you until approved.
                  </div>
                  {(isOwnerView || user?.role === "admin") && (
                    <Button size="sm" className="rounded-full" data-testid="approve-all-drafts-btn"
                            disabled={approvingDrafts} onClick={approveAllDrafts}>
                      {approvingDrafts ? "Approving…" : "Approve all"}
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-10">
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} data-testid={`products-group-${cat.slice(0, 24)}`}>
                  <div className="eyebrow mb-4">{cat}</div>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                    {items.map((p) => (
                      <div key={p.product_id} data-testid={`product-${p.product_id}`}
                           className={`border-b border-[#E8E2D9] pb-4 ${p.ai_draft ? "bg-[#FFF8EA]/60 rounded-lg -mx-2 px-2" : ""}`}>
                        <div className="flex items-baseline justify-between gap-4">
                          <div className="font-serif text-lg leading-tight">
                            {p.title}
                            {p.ai_draft && (
                              <span className="ml-2 text-[10px] uppercase tracking-widest bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 rounded-full align-middle" data-testid={`product-draft-badge-${p.product_id}`}>Draft</span>
                            )}
                          </div>
                          <div className="text-sm text-[#1A1A1A] whitespace-nowrap">
                            {p.price > 0
                              ? `$${Number(p.price).toLocaleString()}`
                              : (p.ai_starting_price ? `From $${Number(p.ai_starting_price).toLocaleString()}` : "")}
                          </div>
                        </div>
                        {p.images?.[0] && (
                          <img src={p.images[0]} alt={p.title} loading="lazy" data-testid={`product-image-${p.product_id}`}
                               className="mt-3 w-full h-44 object-cover rounded-md" />
                        )}
                        {p.description && <p className="text-sm text-[#5C5C5C] mt-1 leading-snug">{p.description}</p>}
                        {(isOwnerView || user?.role === "admin") && (
                          <>
                            <ProductImageEditor product={p} onSaved={() => loadProducts(v)} />
                            <ProductOwnerEditor product={p} onChanged={() => loadProducts(v)} />
                          </>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                          <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] bg-[#EFEADF] px-2 py-0.5 rounded-full">{p.kind}</span>
                          {(p.ai_tags || []).map((t) => (
                            <span key={t} className="text-[10px] uppercase tracking-widest text-[#8A9A86] bg-[#EFEADF] px-2 py-0.5 rounded-full">{t}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Reviews */}
      <section className="mt-16 sm:mt-24 border-t border-[#E8E2D9] pt-12 sm:pt-16">
        <div className="flex items-end justify-between mb-8 sm:mb-10 gap-3 flex-wrap">
          <div>
            <div className="eyebrow mb-3">Reviews</div>
            <h2 className="font-serif text-3xl sm:text-4xl">What planners are saying</h2>
          </div>
          {user && !isOwnerView && (
            <Button data-testid="add-review-btn" variant="outline" className="rounded-full" onClick={() => setReviewOpen(true)}>Write a review</Button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6" data-testid="reviews-list">
          {(v.reviews || []).length === 0 && <div className="text-[#5C5C5C]">No reviews yet.</div>}
          {(v.reviews || []).map((r) => (
            <div key={r.review_id} className="card-elegant p-6">
              <div className="flex items-center justify-between">
                <div className="font-medium">{r.user_name}</div>
                <div className="text-sm">★ {r.rating}</div>
              </div>
              <p className="mt-3 text-[#5C5C5C] leading-relaxed">{r.comment}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Booking Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="booking-dialog" className="max-w-lg bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Request a quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div>
              <div className="eyebrow mb-2">Booking for</div>
              {(events || []).length > 1 && (
                <select
                  data-testid="quote-event-select"
                  value={selectedEventId}
                  onChange={(e) => selectEvent(e.target.value)}
                  className="w-full h-11 rounded-md border border-[#E8E2D9] bg-white px-3 text-sm mb-2"
                >
                  <option value="">Choose another event…</option>
                  {events.map((e) => <option key={e.event_id} value={e.event_id}>{e.title} · {formatEventDate(e.date)}</option>)}
                </select>
              )}
              {selectedEvent ? (
                <div data-testid="quote-event-summary" className="rounded-md border border-[#E8E2D9] bg-white p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate" data-testid="quote-event-title">{selectedEvent.title}</div>
                    <div className="text-sm text-[#5C5C5C] flex items-center gap-1.5 mt-0.5" data-testid="quote-event-date">
                      <CalendarIcon className="w-3.5 h-3.5" /> {formatEventDate(selectedEvent.date, { weekday: "short", year: "numeric", month: "long", day: "numeric" })}
                    </div>
                    {selectedEvent.location && <div className="text-xs text-[#5C5C5C] mt-0.5 truncate">{selectedEvent.location}</div>}
                  </div>
                  {(selectedEvent.vendor_ids || []).length > 0 ? (
                    <span
                      data-testid="change-event-date-locked"
                      title="This event already has bookings. Cancel unresolved requests, use a change order for accepted bookings, or create a new event."
                      className="text-xs text-[color:var(--text-muted)] shrink-0 whitespace-nowrap cursor-not-allowed"
                    >Date locked</span>
                  ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="change-event-date-btn" className="text-xs link-underline text-[#1A1A1A] shrink-0 whitespace-nowrap">Change event date</button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={changeEventDate}
                        disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || isDateBlocked(d)}
                        data-testid="quote-calendar"
                      />
                      <p className="px-3 pb-3 text-[11px] text-[color:var(--text-muted)] max-w-[260px]">Saves the new date to “{selectedEvent.title}” and re-checks availability.</p>
                    </PopoverContent>
                  </Popover>
                  )}
                </div>
              ) : (
                <div data-testid="quote-no-event" className="rounded-md border border-dashed border-[#E8E2D9] bg-white p-3 text-sm text-[#5C5C5C]">
                  {(events || []).length === 0
                    ? <>No upcoming events. <Link to="/planner/dashboard" className="link-underline text-[#1A1A1A]">Create an event first →</Link></>
                    : "Select an event to continue."}
                </div>
              )}
              {avail?.available === false && (
                <div className="mt-2 text-xs text-[color:var(--accent)] flex items-center gap-1.5" data-testid="quote-unavailable-note"><XCircle className="w-3.5 h-3.5" /> {unavailableReason}</div>
              )}
            </div>
            <div>
              <div className="eyebrow mb-2">Time of day</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="quote-period-am"
                  onClick={() => setPeriod("am")}
                  disabled={isPeriodBlocked(date, "am")}
                  className={`h-11 rounded-md border text-sm ${period === "am" ? "bg-[color:var(--accent)] text-white border-[color:var(--accent)]" : "bg-white border-[#E8E2D9]"} ${isPeriodBlocked(date, "am") ? "opacity-40 cursor-not-allowed line-through" : ""}`}
                >Morning (AM)</button>
                <button
                  type="button"
                  data-testid="quote-period-pm"
                  onClick={() => setPeriod("pm")}
                  disabled={isPeriodBlocked(date, "pm")}
                  className={`h-11 rounded-md border text-sm ${period === "pm" ? "bg-[color:var(--accent)] text-white border-[color:var(--accent)]" : "bg-white border-[#E8E2D9]"} ${isPeriodBlocked(date, "pm") ? "opacity-40 cursor-not-allowed line-through" : ""}`}
                >Afternoon / Evening (PM)</button>
              </div>
            </div>
            {requiresDuration && (
              <DurationSelect value={durationHours} onChange={setDurationHours} testid="quote-duration-select" />
            )}
            <div>
              <div className="eyebrow mb-2">Guest count</div>
              <Input type="number" data-testid="guests-input" value={guests} onChange={(e) => setGuests(+e.target.value)} className="bg-white h-11" />
            </div>
            <div>
              <div className="eyebrow mb-2">Message</div>
              <Textarea data-testid="message-input" rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Tell the vendor about your event…" className="bg-white" />
            </div>
            {menuTree.length > 0 && (
              <div data-testid="quote-item-picker">
                <div className="eyebrow mb-2" data-testid="quote-picker-label">{offeringLabels(v.category).quotePickerLabel} <span className="text-[color:var(--text-muted)] normal-case tracking-normal text-[11px]">(optional — informational only)</span></div>
                <div className="max-h-52 overflow-y-auto space-y-2 border border-[color:var(--border)] rounded-lg bg-white p-3">
                  {menuTree.map((m) => (
                    <div key={m.menu_id}>
                      <div className="text-[11px] uppercase tracking-widest text-[color:var(--text-muted)]">{m.name}</div>
                      {(m.categories || []).map((c) => (
                        <div key={c.category_id} className="ml-2 mt-1">
                          <div className="text-[12px] text-[color:var(--text-muted)]">{c.name}</div>
                          {(c.items || []).map((it) => (
                            <label key={it.item_id} className="flex items-center gap-2 text-sm ml-2 py-0.5 cursor-pointer">
                              <input type="checkbox" data-testid={`quote-pick-${it.item_id}`} checked={selectedItems.includes(it.item_id)} onChange={(e) => setSelectedItems(e.target.checked ? [...selectedItems, it.item_id] : selectedItems.filter((x) => x !== it.item_id))} />
                              <span className="flex-1">{it.name}</span>
                              {Number(it.price) > 0 && <span className="text-[color:var(--text-muted)] text-xs">${it.price}{it.pricing_type === "per_person" ? "/person" : ""}</span>}
                            </label>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-[color:var(--text-muted)] mt-1">No payment is due until you accept the vendor's final quote.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="submit-booking-btn" className="btn-sage rounded-full" onClick={submitBooking} disabled={bookingBlocked || durationMissing}>Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-md bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Write a review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {[1,2,3,4,5].map(i => (
                <button key={i} data-testid={`star-${i}`} onClick={() => setRating(i)}
                  className={`w-9 h-9 rounded-full border ${rating >= i ? "bg-[#C05A46] text-white border-[#C05A46]" : "bg-white border-[#E8E2D9]"}`}>★</button>
              ))}
            </div>
            <Textarea data-testid="review-comment" value={comment} onChange={e => setComment(e.target.value)} placeholder="Share your experience…" rows={4} className="bg-white" />
          </div>
          <DialogFooter>
            <Button data-testid="submit-review-btn" className="btn-sage rounded-full" onClick={submitReview}>Post review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// "Booking for" — the planner's upcoming events. One event auto-selects (in the
// parent), several require a choice, none blocks booking until one is created.
function EventPicker({ events, selectedEventId, onSelect, period, onPeriod, requiresDuration, durationHours, onDuration, avail, existingBooking }) {
  const selected = (events || []).find((e) => e.event_id === selectedEventId) || null;
  return (
    <div data-testid="booking-event-picker" className="mt-6 rounded-md border border-[#E8E2D9] bg-[#F5F2EB] p-4 space-y-3">
      <div className="eyebrow flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Booking for</div>
      {events === null ? (
        <div className="text-sm text-[#5C5C5C] flex items-center gap-2" data-testid="booking-events-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading your events…</div>
      ) : events.length === 0 ? (
        <div className="text-sm text-[#5C5C5C]" data-testid="booking-no-events">
          You need an upcoming event to book this vendor.{" "}
          <Link to="/planner/dashboard" data-testid="create-event-first-link" className="link-underline text-[#1A1A1A] font-semibold">Create an event first →</Link>
        </div>
      ) : (
        <>
          {events.length > 1 && (
            <select
              data-testid="booking-event-select"
              value={selectedEventId}
              onChange={(e) => onSelect(e.target.value)}
              className="w-full h-11 rounded-md border border-[#E8E2D9] bg-white px-3 text-sm"
            >
              <option value="">Select an event…</option>
              {events.map((e) => <option key={e.event_id} value={e.event_id}>{e.title} · {formatEventDate(e.date)}</option>)}
            </select>
          )}
          {selected ? (
            <div data-testid="booking-event-summary" className="text-sm">
              <div className="font-semibold truncate" data-testid="booking-event-title">{selected.title}</div>
              <div className="text-[#5C5C5C] mt-0.5" data-testid="booking-event-date">
                {formatEventDate(selected.date, { weekday: "short", year: "numeric", month: "long", day: "numeric" })}
                {selected.location ? ` · ${selected.location}` : ""}
                {Number(selected.guest_count) > 0 ? ` · ${selected.guest_count} guests` : ""}
              </div>
            </div>
          ) : (
            <div className="text-sm text-[#5C5C5C]" data-testid="booking-select-event-hint">Select an event to check availability and book.</div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {[["am", "Morning (AM)"], ["pm", "Afternoon / Evening (PM)"]].map(([k, label]) => (
              <button
                key={k}
                type="button"
                data-testid={`booking-period-${k}`}
                onClick={() => onPeriod(k)}
                className={`h-9 rounded-md border text-xs ${period === k ? "bg-[color:var(--accent)] text-white border-[color:var(--accent)]" : "bg-white border-[#E8E2D9]"}`}
              >{label}</button>
            ))}
          </div>
          {requiresDuration && !existingBooking && (
            <DurationSelect value={durationHours} onChange={onDuration} testid="booking-duration-select" compact />
          )}
          {selected && (
            <div data-testid="event-availability-status" className="text-xs flex items-start gap-1.5">
              {existingBooking ? (
                <><CheckCircle2 className="w-3.5 h-3.5 mt-px text-[color:var(--verified)]" /><span className="text-[color:var(--verified)]" data-testid="event-already-booked">Booked for {formatEventDate(selected.date)} — this is your booking</span></>
              ) : avail?.loading || !avail ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin mt-px text-[#5C5C5C]" /><span className="text-[#5C5C5C]">Checking availability…</span></>
              ) : avail.available ? (
                <><CheckCircle2 className="w-3.5 h-3.5 mt-px text-[color:var(--verified)]" /><span className="text-[color:var(--verified)]" data-testid="event-available">Available for {formatEventDate(selected.date)} ({period.toUpperCase()})</span></>
              ) : (
                <><XCircle className="w-3.5 h-3.5 mt-px text-[color:var(--accent)]" /><span className="text-[color:var(--accent)]" data-testid="event-unavailable">Unavailable for {formatEventDate(selected.date)}{avail.reason ? ` — ${avail.reason}` : ""}</span></>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProductOwnerEditor({ product, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: product.title || "", description: product.description || "", price: product.price ?? 0 });
  const pid = product.product_id;
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      await api.put(`/products/${pid}`, { title: form.title.trim(), description: form.description.trim(), price: Number(form.price) || 0 });
      toast.success("Product updated");
      setEditing(false);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update product"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${product.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/products/${pid}`);
      toast.success("Product deleted");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not delete product"); setBusy(false); }
  };
  return (
    <div className="mt-2" data-testid={`product-owner-controls-${pid}`}>
      {editing ? (
        <div className="space-y-2" data-testid={`product-edit-form-${pid}`}>
          <Input data-testid={`product-edit-title-${pid}`} value={form.title} placeholder="Title"
                 onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white h-9 text-sm" />
          <Textarea data-testid={`product-edit-description-${pid}`} value={form.description} placeholder="Description" rows={2}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white text-sm" />
          <Input data-testid={`product-edit-price-${pid}`} type="number" min="0" step="0.01" value={form.price} placeholder="Price"
                 onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-white h-9 text-sm" />
          <div className="flex gap-2">
            <Button data-testid={`product-edit-save-${pid}`} size="sm" className="h-8 rounded-full text-xs" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
            <Button data-testid={`product-edit-cancel-${pid}`} size="sm" variant="ghost" className="h-8 rounded-full text-xs" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button data-testid={`product-edit-btn-${pid}`} size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => setEditing(true)} disabled={busy}>Edit</Button>
          <Button data-testid={`product-delete-btn-${pid}`} size="sm" variant="ghost" className="h-8 rounded-full text-xs text-red-700 hover:text-red-800" onClick={remove} disabled={busy}>Delete</Button>
        </div>
      )}
    </div>
  );
}

function ProductImageEditor({ product, onSaved }) {
  const [url, setUrl] = useState(product.images?.[0] || "");
  const [busy, setBusy] = useState(false);
  const save = async (images) => {
    setBusy(true);
    try {
      await api.put(`/products/${product.product_id}`, { images });
      toast.success(images.length ? "Image saved" : "Image removed");
      if (!images.length) setUrl("");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save image"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex flex-col sm:flex-row gap-2" data-testid={`product-image-editor-${product.product_id}`}>
      <Input data-testid={`product-image-url-${product.product_id}`} placeholder="Image URL (https://…)" value={url}
             onChange={(e) => setUrl(e.target.value)} className="bg-white h-9 text-sm" />
      <div className="flex gap-2">
        <Button data-testid={`product-image-save-${product.product_id}`} variant="outline" className="h-9 rounded-full text-xs"
                onClick={() => save(url.trim() ? [url.trim()] : [])} disabled={busy || (!url.trim() && !product.images?.[0])}>
          {busy ? "Saving…" : "Save image"}
        </Button>
        {product.images?.[0] && (
          <Button data-testid={`product-image-remove-${product.product_id}`} variant="ghost" className="h-9 rounded-full text-xs"
                  onClick={() => save([])} disabled={busy}>Remove</Button>
        )}
      </div>
    </div>
  );
}
